import { useState } from "react";
import { ArrowLeft, Pencil, ShieldAlert } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCsed, getTaxRecord } from "@/features/transcripts/api";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { TaxPeriodFormDialog } from "@/features/transcripts/components/tax-period-form-dialog";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import type { CsedResult } from "@/features/transcripts/lib/csed";
import type { TaxRecordDetail } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { cn, formatCents } from "@/lib/utils";

interface DetailData {
  record: TaxRecordDetail;
  csed: CsedResult;
}

export default function ClientTranscriptDetailPage() {
  const { client, refreshData } = useTranscriptsContext();
  const { taxRecordId } = useParams<{ taxRecordId: string }>();
  const [editing, setEditing] = useState(false);

  const state = useResource<DetailData>(async () => {
    const [record, csed] = await Promise.all([
      getTaxRecord(client.id, taxRecordId as string),
      getCsed(client.id, taxRecordId as string),
    ]);
    return { record, csed };
  }, [client.id, taxRecordId]);

  return (
    <div className="flex flex-col gap-6">
      <Link to=".." className="text-muted-foreground hover:text-primary flex w-fit items-center gap-1 text-sm">
        <ArrowLeft className="size-4" /> Back to Tax Owed
      </Link>

      <AsyncContent state={state}>
        {({ record, csed }) => (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-foreground mb-2 text-3xl font-bold">
                  {record.form}
                  {record.quarter ? ` Q${record.quarter}` : ""} — {record.taxYear}
                </h1>
                <p className="text-muted-foreground">
                  {record.forWho === "T" ? "Taxpayer" : "Spouse"} · {record.filedStatus}
                  {record.hasLien && (
                    <span className="text-destructive ml-2 inline-flex items-center gap-1">
                      <ShieldAlert className="size-4" /> Lien on file
                    </span>
                  )}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil /> Edit record
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Card className="gap-1 p-4">
                <span className="text-muted-foreground text-xs font-medium uppercase">Balance</span>
                <span className="text-foreground text-xl font-bold">{formatCents(record.balanceCents)}</span>
              </Card>
              <Card className="gap-1 p-4">
                <span className="text-muted-foreground text-xs font-medium uppercase">Interest + Penalty</span>
                <span className="text-foreground text-xl font-bold">
                  {formatCents(record.accruedInterestCents + record.accruedPenaltyCents)}
                </span>
              </Card>
              <Card className="gap-1 p-4">
                <span className="text-muted-foreground text-xs font-medium uppercase">Total Owed</span>
                <span className="text-foreground text-xl font-bold">{formatCents(record.totalOwedCents)}</span>
              </Card>
            </div>

            <Card className="gap-4 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-foreground text-sm font-semibold">
                  Collection Statute Expiration Date (CSED)
                </h3>
                {csed.tollingActive && (
                  <Badge variant="outline" className="border-warning/20 bg-warning/10 text-warning text-[11px] font-bold uppercase">
                    Tolling Active
                  </Badge>
                )}
              </div>
              {csed.dueDates.length === 0 ? (
                <p className="text-muted-foreground text-sm">No CSED calculated — no qualifying assessment on this record.</p>
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {csed.dueDates.map((dd) => (
                    <li key={`${dd.triggerCode}-${dd.triggerDate}`} className="bg-muted/40 rounded-lg px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Trigger {dd.triggerCode} on {dd.triggerDate}:</span>{" "}
                      <span className="text-foreground font-semibold">{dd.dueDate}</span>
                    </li>
                  ))}
                </ul>
              )}
              {csed.tollingPeriods.length > 0 && (
                <div className="border-t pt-3">
                  <h4 className="text-muted-foreground mb-2 text-xs font-semibold uppercase">Tolling Events</h4>
                  <ul className="flex flex-col gap-1.5 text-sm">
                    {csed.tollingPeriods.map((period, index) => (
                      <li key={index} className="text-muted-foreground flex items-center justify-between gap-2">
                        <span className="capitalize">{period.type.replace(/-/g, " ")}</span>
                        <span>
                          {period.startDate} → {period.endDate}
                          {period.extraDays > 0 && ` (+${period.extraDays}d)`}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] font-bold uppercase",
                            period.status === "Tolling"
                              ? "border-warning/20 bg-warning/10 text-warning"
                              : "border-border bg-muted text-muted-foreground",
                          )}
                        >
                          {period.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            <Card className="gap-0 overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Date</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Explanation</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {record.transactions.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-muted-foreground">{item.date}</TableCell>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.explanation}</TableCell>
                      <TableCell className="text-right">{formatCents(item.amountCents)}</TableCell>
                    </TableRow>
                  ))}
                  {record.transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground text-center">
                        No transaction history for this record.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>

            <TaxPeriodFormDialog
              clientId={client.id}
              hasSpouse={!!client.spouse}
              record={record}
              forWho={record.forWho}
              open={editing}
              onOpenChange={setEditing}
              onSaved={() => {
                state.reload();
                refreshData();
              }}
            />
          </>
        )}
      </AsyncContent>
    </div>
  );
}
